import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import ReceiptTicket from "../components/ReceiptTicket";

function startOfDay(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfDay(dateStr) {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return d;
}

export default function ReceiptsList() {
  const [from, setFrom] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [to, setTo] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [supplier, setSupplier] = useState("");
  const [rows, setRows] = useState([]); // docs receipts
  const [loading, setLoading] = useState(false);

  const [active, setActive] = useState(null); // receipt sélectionné pour aperçu
  const [itemsMap, setItemsMap] = useState({});
  const printRef = useRef(null);

  const spanLabel = useMemo(() => {
    const f = new Date(from).toLocaleDateString();
    const t = new Date(to).toLocaleDateString();
    return `${f} → ${t}`;
  }, [from, to]);

  async function fetchReceipts() {
    setLoading(true);
    try {
      const fDate = startOfDay(from);
      const tDate = endOfDay(to);
      const qy = query(
        collection(db, "receipts"),
        where("createdAt", ">=", fDate),
        where("createdAt", "<=", tDate),
        orderBy("createdAt", "desc"),
        limit(200)
      );

      const snap = await getDocs(qy);
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (supplier.trim()) {
        const s = supplier.trim().toLowerCase();
        list = list.filter((r) =>
          (r.supplierName || "").toLowerCase().includes(s)
        );
      }
      setRows(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openPreview(r) {
    setActive(r);
    // Charger des métadonnées articles (nom / barcode) pour l'aperçu
    const ids = Array.from(new Set((r.items || []).map((it) => it.itemId)));
    const map = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "itemsEK", id));
          if (snap.exists()) map[id] = snap.data();
        } catch (e) {
          /* ignore */
        }
      })
    );
    setItemsMap(map);
  }

  function print() {
    window.print();
  }

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-0">📄 Réceptions</h3>
          <small className="text-muted">Période: {spanLabel}</small>
        </div>
        <div className="d-flex gap-2">
          <input
            type="date"
            className="form-control"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <input
            type="date"
            className="form-control"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <input
            className="form-control"
            placeholder="Fournisseur"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          />
          <button
            className="btn btn-outline-dark"
            onClick={fetchReceipts}
            disabled={loading}
          >
            {loading ? "..." : "Filtrer"}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Réf</th>
                  <th>PO</th>
                  <th>Fournisseur</th>
                  <th className="text-end">Lignes</th>
                  <th className="text-end">Qté</th>
                  <th className="text-end">Coût Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-muted">
                      Aucune réception
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const qty = (r.items || []).reduce(
                      (s, it) => s + Number(it.qtyReceived || 0),
                      0
                    );
                    const cost = (r.items || []).reduce(
                      (s, it) =>
                        s +
                        Number(it.qtyReceived || 0) *
                          Number(it.cost || 0),
                      0
                    );
                    const created =
                      r?.createdAt?.toDate?.() || r?.createdAt || new Date();
                    const date = new Date(created).toLocaleString();

                    return (
                      <tr key={r.id}>
                        <td>{date}</td>
                        <td>#{r.id.slice(0, 8)}</td>
                        <td>{r.poId || "manual"}</td>
                        <td>{r.supplierName || "—"}</td>
                        <td className="text-end">
                          {(r.items || []).length}
                        </td>
                        <td className="text-end">
                          {qty.toLocaleString()}
                        </td>
                        <td className="text-end">
                          {cost.toLocaleString()} XOF
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => openPreview(r)}
                          >
                            Aperçu
                          </button>
                          <button
                            className="btn btn-sm btn-dark"
                            onClick={() => {
                              setActive(r);
                              setTimeout(print, 50);
                            }}
                          >
                            Imprimer
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Aperçu / Zone imprimable */}
      {active && (
        <div className="mt-3 print-area" ref={printRef}>
          <ReceiptTicket
            receipt={active}
            itemsMap={itemsMap}
            locationName="Boutique"
          />
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area {
            position: absolute; left: 0; top: 0; width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
